import type {
  AccessRequestOf,
  ArgsOf,
  Parameter,
  RequestOf,
  StagedRequestOf,
} from "../../../access/types.ts";
import { Access, Delegate } from "../../../access/types.ts";
import { AwsAccesses } from "../aws/accesses.ts";
import { GcloudAccesses } from "../gcloud/accesses.ts";

const Resource = {
  type: "object",
  label: "Kubernetes resource data",
  properties: {
    kind: {
      type: "generated",
      label: "Kubernetes 'kind' (e.g. 'pod')",
    } as const,
    name: {
      type: "generated",
      label: "Object name",
    } as const,
    namespace: {
      type: "generated",
      label: "Kubernetes namespace",
      optional: true,
    } as const,
  },
} as const satisfies Parameter;

export const KubernetesAccesses = {
  resource: Access({
    description: "Request a role with permissions on a Kubernetes resource",
    label: "Kubernetes resource",
    delegation: {
      aws: Delegate({
        accesses: AwsAccesses,
        key: "resource" as const,
        resource: "aws",
      }),
      gcp: Delegate({
        accesses: GcloudAccesses,
        key: "role" as const,
        resource: "gcloud",
      }),
    },
    generated: {
      role: { type: "string" },
    },
    maturity: "ga",
    parameters: {
      cluster: {
        type: "static",
        description: "ID of the Kubernetes cluster as configured in P0",
        label: "Cluster ID",
        optional: true,
      },
      clusterId: {
        type: "generated",
        description: "ID of the Kubernetes cluster as configured in P0",
        label: "Cluster ID",
      },
      locator: {
        type: "dynamic",
        description:
          "Locator in format 'kind / namespace / name'; use '*' as a wildcard; defaults to '* / * / *'",
        label: "Resource locator",
        optional: true,
      },
      resource: Resource,
      role: {
        type: "dynamic",
        description: "Kubernetes or P0-curated role name",
        label: "Role name",
      },
    },
    render: { locator: "clusterId", policy: "role" } as const,
  }),
};

export type KubernetesAccesses = typeof KubernetesAccesses;
export type KubernetesAccess = KubernetesAccesses[keyof KubernetesAccesses];

export type KubernetesResourceAccess = KubernetesAccesses["resource"];

export type KubernetesRequest = RequestOf<KubernetesAccess>;
export type KubernetesResourceRequest = RequestOf<KubernetesResourceAccess>;
export type StagedKubernetesResourceRequest =
  StagedRequestOf<KubernetesResourceAccess>;

export type KubernetesAccessRequest = AccessRequestOf<KubernetesAccesses>;

export type KubernetesArgs = ArgsOf<KubernetesAccess>;

export type KubernetesResourceId =
  KubernetesResourceRequest["permission"]["resource"];
